import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { apiBaseUrl } from '../../config';
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import axios from 'axios';
import parse from "html-react-parser";

import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

const ServicesPage = () => {
  const isCarouselInitialized = useRef(false);
  const isDoctorCarouselInitialized = useRef(false);
  const [isCarouselReady, setIsCarouselReady] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState("collapseOne");
  const [cms, setCms] = useState([]);
  
  // Dynamic data states
  const [evidence, setEvidence] = useState([]);
  const [solutions, setSolutions] = useState([]);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  // Fetch CMS
  const fetchCms = async () => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/all-get-cms`);
  
      if (data.status) {
        const cmsData = {};
        data.cms_pages.forEach((item) => {
          cmsData[item.cms_id] = item;
        });
        setCms(cmsData);
      } else {
        setCms({});
      }
    } catch (err) {
      console.error("Error fetching CMS pages:", err);
      setCms({});
    }
  };

  // Fetch all items from database
  const fetchItems = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/items`, {
        params: { limit: 1000 }
      });
      
      if (response.data.status) {
        const allItems = response.data.data;
        setEvidence(allItems.filter(item => item.type === 'services_evidence'));
        setSolutions(allItems.filter(item => item.type === 'services_solution'));
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  useEffect(() => {
    fetchCms();
    fetchItems();
  }, []);

  // Load carousel
  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const jQueryModule = await import("jquery");
        const $ = jQueryModule.default || jQueryModule;
        window.jQuery = $;
        window.$ = $;
        await import("owl.carousel");
        setIsCarouselReady(true);
        setTimeout(() => {
          initializeCarousels();
        }, 500);
      } catch (error) {
        console.error("Error loading carousel:", error);
        setIsCarouselReady(false);
      }
    };

    const initializeCarousels = () => {
      const $ = window.$;
      if (!$ || !$.fn || !$.fn.owlCarousel) {
        console.error("jQuery or owlCarousel not available");
        return;
      }

      if (!isCarouselInitialized.current) {
        const testimonialElement = $(".testimonial-slider");
        if (testimonialElement.length) {
          try {
            testimonialElement.owlCarousel({
              items: 1,
              loop: true,
              autoplay: true,
              autoplayTimeout: 4000,
              smartSpeed: 800,
              dots: true,
              nav: false,
              margin: 30,
            });
            isCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing testimonial carousel:", e);
          }
        }
      }

      if (!isDoctorCarouselInitialized.current) {
        const doctorElement = $(".doctor-slider");
        if (doctorElement.length) {
          try {
            doctorElement.owlCarousel({
              loop: true,
              margin: 25,
              nav: false,
              dots: false,
              autoplay: true,
              autoplayTimeout: 3000,
              smartSpeed: 800,
              responsive: {
                0: { items: 1 },
                576: { items: 2 },
                992: { items: 3 },
                1200: { items: 4 },
              },
            });
            isDoctorCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing doctor carousel:", e);
          }
        }
      }
    };

    loadCarousel();

    return () => {
      const $ = window.$;
      if ($ && $.fn && $.fn.owlCarousel) {
        try {
          if (isCarouselInitialized.current) {
            $(".testimonial-slider").owlCarousel("destroy");
            isCarouselInitialized.current = false;
          }
          if (isDoctorCarouselInitialized.current) {
            $(".doctor-slider").owlCarousel("destroy");
            isDoctorCarouselInitialized.current = false;
          }
        } catch (e) {
          console.log("Carousel already destroyed or not initialized");
        }
      }
    };
  }, []);

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  return (
    <div>
      <Helmet>
        <title>Predyxiq Services</title>
      </Helmet>

      {/* ============================================ */}
      {/* HERO SECTION - CMS ID 65 */}
      {/* ============================================ */}
      <section className="hero-section" style={{ padding: "40px 0px" }}>
        <div className="container text-center">
          <h2 className="predyx-main-title mb-3" style={{ lineHeight: "43px" }}>
            {parse(cms[65]?.cms_page_heading || "Healthcare is not delivered by a single organisation, <br /><span>technology or professional.</span>")}
          </h2>
          {parse(cms[65]?.cms_page_description || "")}
          <a href="#" className="action-items-primary-btn mr-2">
            {cms[65]?.cms_page_small_description || "Explore the Solution"}
            <i className="fas fa-chevron-right" />
          </a>

          <img
            src={`${apiBaseUrl}/assets/images/${cms[65]?.cms_image}`}
            alt="About us"
            className="img-fluid"
            style={{ marginTop: "20px", borderRadius: "10px" }}
          />
        </div>
      </section>

      {/* ============================================ */}
      {/* BUILT ONCE APPLIED EVERYWHERE - CMS ID 66 */}
      {/* ============================================ */}
      <section className="explain-section predyx-section">
        <div className="container">
          <div className="predyx-header text-center mb-4">
            <h2 className="predyx-main-title mt-2">
              {parse(cms[66]?.cms_page_heading || "Built Once. <span>Applied Everywhere.</span>")}
            </h2>
          </div>
          <div className="predyx-content-box">
            {parse(cms[66]?.cms_page_description || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* BUILT TO INTEGRATE - CMS ID 67 */}
      {/* ============================================ */}
      <section className="explain-section" style={{ background: "#f9fcff" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center">
              <h2 className="section-title">
                {parse(cms[67]?.cms_page_heading || "Built to Integrate. <span>Never to Replace.</span>")}
              </h2>
            </div>
          </div>

          <div className="decision-card" style={{ marginTop: "20px" }}>
            <div className="row">
              <div className="col-lg-4">
                <div className="info-card">
                  <h5>Healthcare already possesses extraordinary expertise:</h5>
                  <ul>
                    {evidence.map((item, index) => (
                      <li key={index}>{item.title}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="col-lg-8">
                <div className="analysis-card">
                  <div className="analysis-block">
                    {parse(cms[67]?.cms_page_description || "")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* OUR SOLUTIONS - CMS ID 68 + Items */}
      {/* ============================================ */}
      <section className="does-section">
        <div className="container">
          <div className="text-center mb-2">
            <h2 className="section-title">{parse(cms[68]?.cms_page_heading || "Our <span>Solutions</span>")}</h2>
            <h3 className="section-subtitle" style={{ fontSize: "20px" }}>
            {parse(cms[68]?.cms_page_small_description || "Predyxiq is a single Clinical Intelligence Platform with purpose-built solutions for the healthcare ecosystem.")}
            </h3>
            <p className="section-text">
            {parse(cms[68]?.cms_page_description || "Select the solution most relevant to your organisation to explore how Predyxiq integrates into your workflow.")}
            </p>
          </div>
          <div className="row align-items-center">
            <div className="col-lg-7">
              <div className="timeline">
                {solutions.map((item, index) => (
                  <div className="timeline-item" key={index}>
                    <div className="number">{index + 1}</div>
                    <div className="content">
                      <h5>{item.title}</h5>
                      <p style={{ marginBottom: "9px" }}>{item.description}</p>
                      <Link to={`/solutions/${item.id}`} className="service-read-more">
                        Explore Solution →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-5 text-center">
              <img
                src={`${apiBaseUrl}/assets/images/${cms[68]?.cms_image || 'clinical2.png'}`}
                className="img-fluid side-image"
                alt="Medical illustration"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER CTA - CMS ID 69 */}
      {/* ============================================ */}
      <section className="funfacts" ref={ref}>
        <div className="overlay">
          <div className="container" style={{ textAlign: "center" }}>
            <h2 style={{ fontSize: "28px", color: "#fff", fontWeight: "600", marginBottom: "20px" }}>
              {parse(cms[69]?.cms_page_heading || "One Platform. One Scientific Foundation.")}
            </h2>
            <p style={{ fontSize: "16px", color: "#fff", marginBottom: "30px" }}>
              {parse(cms[69]?.cms_page_small_description || "Different users.<br/>Different workflows.<br/>One scientific foundation.")}
            </p>
            {parse(cms[69]?.cms_page_description || "")}
         
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;